import { useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import {
  DrawerContent,
  DrawerFooter,
  DrawerTopBar,
  SwipeableDrawer,
} from "@/components/Drawer";
import { PriceRangeSlider } from "@/components/Sliders/PriceRangeSlider";
import type { IHotelsFilters } from "@/features/hotels/types/types";
import { rudderAnalytics, HotelFiltersTrackingEvents } from "@/lib/rudderstack";

type PriceFilterDrawerContentsProps = {
  defaultFilters: IHotelsFilters;
  filters: IHotelsFilters;
  bookingNights: number;
  onApply: (newPriceRange: [number, number]) => void;
  onClose: () => void;
};

function PriceFilterDrawerContents({
  defaultFilters,
  filters,
  bookingNights,
  onApply,
  onClose,
}: PriceFilterDrawerContentsProps) {
  const intl = useIntl();
  const nights = Math.max(1, bookingNights);
  const [localPriceRange, setLocalPriceRange] = useState<[number, number]>(
    filters.priceRange,
  );
  const [inputMethod, setInputMethod] = useState<"slider" | "text">("slider");

  return (
    <>
      <DrawerTopBar
        onCloseClick={onClose}
        title={intl.formatMessage({
          defaultMessage: "Price",
          description: "Title for the price filter drawer",
        })}
      />

      <DrawerContent>
        <p className="mb-4 text-lg font-medium">
          {intl.formatMessage({
            defaultMessage: "Price (per night)",
            description:
              "Label for the price range slider in hotel filters, indicating that the price is calculated per night",
          })}
        </p>

        <PriceRangeSlider
          min={defaultFilters.priceRange[0]}
          max={defaultFilters.priceRange[1]}
          value={[localPriceRange[0], localPriceRange[1]]}
          scale={(value) => value / nights}
          descale={(value) => Math.round(value * nights)}
          onChange={(newValue, source) => {
            setInputMethod(source || "slider");
            setLocalPriceRange(newValue);
          }}
        />
      </DrawerContent>

      <DrawerFooter>
        <Button
          fullWidth
          onClick={() => {
            rudderAnalytics.track(
              HotelFiltersTrackingEvents.USER_CLICKED_ON_APPLY_PRICE,
              {
                min_price_iqd: Math.round(localPriceRange[0] / nights),
                max_price_iqd: Math.round(localPriceRange[1] / nights),
                input_method: inputMethod,
              },
            );
            onApply(localPriceRange);
            onClose();
          }}
          variant="contained"
          size="large"
        >
          {intl.formatMessage({
            defaultMessage: "Apply",
            description: "Button to apply selected filters or changes",
          })}
        </Button>
      </DrawerFooter>
    </>
  );
}

type Props = PriceFilterDrawerContentsProps & {
  open: boolean;
};

export function PriceFilterDrawer({ open, ...props }: Props) {
  return (
    <SwipeableDrawer open={open} onClose={props.onClose}>
      <PriceFilterDrawerContents {...props} />
    </SwipeableDrawer>
  );
}
